import Executive from '../../framework/rmi-util'
import { register } from '../../framework/rmi-server-util'
import { CheckPolicy, Resource } from '../../framework/permission-util'
import { RT } from '../permissions'
import { ownershipTransferTechnical } from './OwnershipTransferTechnical'
import type { OwnershipTransferRequest, OwnershipTransferResponse } from './OwnershipTransferTechnical'

/**
 * Ownership Transfer Executive
 * 
 * Handles high-level business operations for transferring ownership
 * of tour operator organizations from main users to invited admin users.
 */
export default class OwnershipTransferExecutive extends Executive {

	initServer() {}

	/**
	 * Transfer Organization Ownership
	 * 
	 * Transfers complete ownership of a tour operator organization from the
	 * current main user to an invited admin user. This operation:
	 * 
	 * - Updates the main user's email and name across all systems
	 * - Converts the invited admin user to the new main user
	 * - Maintains data consistency across Content Manager, Keycloak, and PEAK
	 * 
	 * This is a transactional operation that will rollback on any failure.
	 * 
	 * @param request - Ownership transfer details
	 * @returns Transfer result with success status and any errors
	 */
	@CheckPolicy()
	async transferOrganizationOwnership(
		@Resource(RT.CLIENT) clientId: string,
		@Resource(RT.PROVIDER) serviceProviderId: string,
		request: OwnershipTransferRequest
	): Promise<OwnershipTransferResponse> {
		return await ownershipTransferTechnical.transferOwnership({
			...request,
			clientId,
			serviceProviderId
		})
	}

	/**
	 * Validate Ownership Transfer Eligibility
	 * 
	 * Checks if an ownership transfer is possible between the current main user
	 * and a target invited user. Validates:
	 * 
	 * - Current user is the main user (no mainUserId)
	 * - Target user exists and has accepted invitation
	 * - Target user has Admin role
	 * - Target user is linked to current main user
	 * - Users are not the same person
	 * 
	 * @param request - Transfer request to validate
	 * @returns Validation result
	 */
	@CheckPolicy()
	async validateTransferEligibility(
		@Resource(RT.CLIENT) clientId: string,
		@Resource(RT.PROVIDER) serviceProviderId: string,
		request: OwnershipTransferRequest
	): Promise<{
		eligible: boolean
		reasons?: string[]
	}> {
		// Use the same validation logic from technical layer but don't execute
		const result = await ownershipTransferTechnical['validateTransferRequest']({
			...request,
			clientId,
			serviceProviderId
		})
		
		return {
			eligible: result.valid,
			reasons: result.errors
		}
	}
}

register(OwnershipTransferExecutive)
